import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import Foldout from 'components/shared/Foldout';
import Search from 'components/shared/Search';
import ControlledTable from 'components/shared/table/ControlledTable';
import { useDebounce } from 'hooks/useDebounce';
import usePagination from 'hooks/usePagination';
import { DenialList } from 'models/DenialList';
import { DenialListItem } from 'models/DenialListItem';
import { PERMISSIONS } from 'models/Profile';
import { adminDenialListItemQueryKeys, indexAdminDenialListItems } from 'services/api/admin/denialListItems';
import { serializeSort } from 'stores/helpers';
import { colors } from 'styles/theme/colors';
import PlusIcon from 'svg/PlusIcon';

import denialListColumns from '../../denialList/denialListColumns';

import AdminDenialListDeleteConfirm from './AdminDenialListDeleteConfirm';
import AdminDenialListEntryForm from './AdminDenialListEntryForm';
import AdminDenialListRowMenu from './AdminDenialListRowMenu';

// Mirrors the customer Denial List page default (Date Added desc, NULLS LAST server-side).
const DEFAULT_SORT: SortingState = [{ id: 'dateAdded', desc: true }];

// The in-panel views: the item list (default) and the inline add / edit / delete flows. The admin
// mockups render add/edit/delete inside the slideout itself rather than as centered modals.
type SlideoutView =
  | { type: 'list' }
  | { type: 'add' }
  | { type: 'edit'; item: DenialListItem }
  | { type: 'delete'; item: DenialListItem };

type Props = {
  denialList: DenialList;
  onClose: () => void;
};

export default function GlobalDenialListSlideout({ denialList, onClose }: Props) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<SlideoutView>({ type: 'list' });
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>(DEFAULT_SORT);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  useEffect(() => {
    requestAnimationFrame(() => setOpen(true));
  }, []);

  // Reset to the first page on each new search and, while searching, relevance-rank
  // results (sort_by=search) the way the customer Denial List page does.
  useEffect(() => {
    pagination.setPageIndex(0);
    setSort(debouncedSearch ? [{ id: 'search', desc: true }] : DEFAULT_SORT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const animateClose = () => {
    setOpen(false);
    setTimeout(onClose, 300);
  };

  const queryParams = useMemo(
    () => ({
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search: debouncedSearch,
    }),
    [pagination.paging, sort, debouncedSearch]
  );

  const { data, isLoading } = useQuery({
    queryKey: adminDenialListItemQueryKeys.index(denialList.id, queryParams),
    queryFn: ({ signal }) => indexAdminDenialListItems(denialList.id, queryParams, signal),
  });

  // An archived list is read-only: no adding patients and no per-row actions.
  const itemActionsDisabled = denialList.archived;

  // Narrower Reason column so the item table fits the slideout panel without horizontal scroll
  // (other columns sum to 540px; the panel's usable width is ~832px). The admin row menu gates the
  // kebab on the admin item permissions (View Only Olio Admins see none).
  const columns = useMemo(
    () =>
      denialListColumns({
        reasonSize: 270,
        rowActionsDisabled: itemActionsDisabled,
        onEdit: (item) => setView({ type: 'edit', item }),
        onDelete: (item) => setView({ type: 'delete', item }),
        RowMenu: AdminDenialListRowMenu,
      }),
    [itemActionsDisabled]
  );

  // Cancel / success returns to the item list (the slideout stays open); the header X closes it.
  const backToList = () => setView({ type: 'list' });

  return (
    <Foldout open={open} position='right' onBackdropClick={animateClose} width='min(92%, 880px)'>
      <PanelContainer>
        <PanelHeader>
          <TitleGroup>
            <PanelTitle>{denialList.client?.name} &ndash; Global Denial List</PanelTitle>
            <PatientCount>{data?.meta.totalRecords ?? <>&mdash;</>} patients</PatientCount>
          </TitleGroup>
          <HeaderActions>
            {view.type === 'list' && (
              // Hidden entirely for View Only Olio Admins; disabled (but shown) for archived lists.
              <AccessGuard accessor={PERMISSIONS.adminDenialListItemCreate}>
                <Button variant='primary' disabled={itemActionsDisabled} onClick={() => setView({ type: 'add' })}>
                  <PlusIcon width={16} height={16} />
                  Add New Patient
                </Button>
              </AccessGuard>
            )}
            <CloseButton onClick={animateClose}>&times;</CloseButton>
          </HeaderActions>
        </PanelHeader>

        {view.type === 'list' && (
          <>
            <PanelToolbar>
              <SearchContainer>
                <Search value={search} onChange={setSearch} placeholder='Search Patient' />
              </SearchContainer>
            </PanelToolbar>

            <PanelContent>
              <ControlledTable<DenialListItem>
                data={data?.data ?? []}
                columns={columns}
                loading={isLoading}
                pageCount={data?.meta.totalPages ?? 1}
                pagination={pagination}
                sorting={{ sortBy: sort, setSortBy: setSort }}
              />
            </PanelContent>
          </>
        )}

        {(view.type === 'add' || view.type === 'edit') && (
          <PanelContent>
            <AdminDenialListEntryForm
              denialList={denialList}
              denialListItem={view.type === 'edit' ? view.item : undefined}
              onClose={backToList}
            />
          </PanelContent>
        )}

        {view.type === 'delete' && (
          <PanelContent>
            <AdminDenialListDeleteConfirm denialListItem={view.item} onClose={backToList} />
          </PanelContent>
        )}
      </PanelContainer>
    </Foldout>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const PatientCount = styled.span`
  font-size: 16px;
  color: ${colors.black50};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const PanelToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px 0;
`;

const SearchContainer = styled.div`
  width: 280px;
`;

const PanelContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px 24px;
`;
